// WhatsApp Message ID Service
// Centralized service for handling WhatsApp message identification and comparison
// Avoids complex serialized key comparisons and provides fine-grained message relevance detection

import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { MessageAckUpdate } from "./message-log-update-service";
import { WhatsappId } from "@bt-common/interfaces/whatsapp-types";

/**
 * Structured representation of WhatsApp message ID components
 */
export interface WhatsAppMessageComponents {
  /** Core unique identifier for the message */
  messageId: string;
  /** Chat/group identifier where the message belongs */
  chatId: string | WhatsappId;
  /** Whether the message was sent by us */
  fromMe: boolean;
  /** For group messages, the participant who sent the message */
  participant?: string;
  /** Original serialized key (for reference only) */
  originalSerialized?: string;
}

/**
 * Recipient-level message status for group messages
 */
export interface RecipientMessageStatus {
  recipientId: string;
  status: 'sent' | 'delivered' | 'read' | 'played';
  timestamp: string;
}

/**
 * Enhanced message tracking for group messages
 */
export interface GroupMessageTracking {
  messageId: string;
  chatId: string;
  totalRecipients: number;
  recipientStatuses: RecipientMessageStatus[];
  aggregatedCounts: {
    sent: number;
    delivered: number;
    read: number;
    played: number;
  };
}

/**
 * Message relevance check result
 */
export interface MessageRelevanceResult {
  isRelevant: boolean;
  matchedMessageId?: string;
  matchType: 'exact_match' | 'component_match' | 'no_match';
  confidence: number; // 0-1 scale
}

/**
 * Centralized service for WhatsApp message ID operations
 */
export class WhatsAppMessageIdService {
  
  /**
   * Parse WhatsApp message ID into meaningful components
   * @param waMessageId WhatsApp message ID object or serialized string
   * @returns Structured message components
   */
  static parseWhatsAppMessageId(waMessageId: any): WhatsAppMessageComponents | null {
    try {
      let messageId: string;
      let chatId: string;
      let fromMe: boolean;
      let participant: string | undefined;
      let originalSerialized: string;

      if (typeof waMessageId === 'string') {
        // Handle serialized string format
        originalSerialized = waMessageId;
        const parts = this.parseSerializedId(waMessageId);
        if (!parts) return null;
        
        messageId = parts.messageId;
        chatId = parts.chatId;
        fromMe = parts.fromMe;
        participant = parts.participant;
      } else if (typeof waMessageId === 'object' && waMessageId !== null) {
        // Handle object format from WhatsApp Web.js
        messageId = waMessageId.id || waMessageId._serialized;
        chatId = waMessageId.remote || waMessageId.to || waMessageId.from;
        fromMe = waMessageId.fromMe || false;
        participant = waMessageId.participant;
        originalSerialized = waMessageId._serialized;

        // If we have the structured object, prefer its components
        if (waMessageId.id && waMessageId.remote !== undefined) {
          messageId = waMessageId.id;
          chatId = waMessageId.remote;
        }
      } else {
        return null;
      }

      if (!messageId || !chatId) {
        return null;
      }

      return {
        messageId,
        chatId,
        fromMe,
        participant,
        originalSerialized
      };
    } catch (error) {
      console.error('[WhatsAppMessageIdService] Error parsing message ID:', error);
      return null;
    }
  }

  /**
   * Parse serialized WhatsApp message ID string
   * Format examples:
   * - "true_972544325389-1618902641@g.us_3EB0722E602AEF7073BD90_972544325389@c.us"
   * - "false_972544325389@c.us_3EB0722E602AEF7073BD90"
   */
  private static parseSerializedId(serialized: string): {
    messageId: string;
    chatId: string;
    fromMe: boolean;
    participant?: string;
  } | null {
    try {
      const parts = serialized.split('_');
      if (parts.length < 3) return null;

      const fromMe = parts[0] === 'true';
      const chatId = parts[1];
      const messageId = parts[2];
      const participant = parts.length > 3 ? parts[3] : undefined;

      return {
        messageId,
        chatId,
        fromMe,
        participant
      };
    } catch (error) {
      console.error('[WhatsAppMessageIdService] Error parsing serialized ID:', error);
      return null;
    }
  }

  /**
   * Find our internal message ID by matching WhatsApp message components
   * @param waComponents WhatsApp message components
   * @param userMessages Array of user messages from Redux store
   * @param messageLogs Array of message logs from Redux store
   * @returns Our internal message ID if found
   */
  static findMessageByComponents(
    waComponents: WhatsAppMessageComponents,
    userMessages: any[],
    messageLogs: any[]
  ): MessageRelevanceResult {
    try {
      // Method 1: Direct component matching in user messages
      const exactMatch = userMessages.find(msg => {
        if (!msg?.waMessageKey) return false;
        
        const msgComponents = this.parseWhatsAppMessageId(msg.waMessageKey);
        if (!msgComponents) return false;

        // Normalize chatIds to strings for comparison
        const msgChatId = msgComponents.chatId;
        const waChatId = typeof waComponents.chatId === 'string' 
          ? waComponents.chatId 
          : waComponents.chatId?._serialized || waComponents.chatId;

        const isMatch = msgComponents.messageId === waComponents.messageId &&
                       msgChatId === waChatId &&
                       msgComponents.fromMe === waComponents.fromMe;

        // Debug logging for message matching (can be removed in production)
        // console.log(`🔍 [RealTimeMessageAckHandler] Comparing user message ${msg._id}:`, {
        //   incoming: { messageId: waComponents.messageId, chatId: waComponents.chatId, fromMe: waComponents.fromMe },
        //   stored: { messageId: msgComponents.messageId, chatId: msgComponents.chatId, fromMe: msgComponents.fromMe },
        //   isMatch
        // });

        return isMatch;
      });

      if (exactMatch) {
        return {
          isRelevant: true,
          matchedMessageId: exactMatch._id,
          matchType: 'exact_match',
          confidence: 1.0
        };
      }

      // Method 2: Component matching in message logs
      const logMatch = messageLogs.find(log => {
        if (!log?.waMessageKey) return false;
        
        const logComponents = this.parseWhatsAppMessageId(log.waMessageKey);
        if (!logComponents) return false;

        return logComponents.messageId === waComponents.messageId &&
               logComponents.chatId === waComponents.chatId;
      });

      if (logMatch) {
        return {
          isRelevant: true,
          matchedMessageId: logMatch.messageId,
          matchType: 'exact_match',
          confidence: 0.9
        };
      }

      // Method 3: Partial matching by message ID only (less reliable)
      const partialMatch = userMessages.find(msg => {
        if (!msg?.waMessageKey) return false;
        
        const msgComponents = this.parseWhatsAppMessageId(msg.waMessageKey);
        if (!msgComponents) return false;

        return msgComponents.messageId === waComponents.messageId;
      });

      if (partialMatch) {
        return {
          isRelevant: true,
          matchedMessageId: partialMatch._id,
          matchType: 'component_match',
          confidence: 0.7
        };
      }

      return {
        isRelevant: false,
        matchType: 'no_match',
        confidence: 0
      };
    } catch (error) {
      console.error('[WhatsAppMessageIdService] Error finding message by components:', error);
      return {
        isRelevant: false,
        matchType: 'no_match',
        confidence: 0
      };
    }
  }

  /**
   * Determine if a WhatsApp message update is relevant to our campaigns
   * @param waMessageId WhatsApp message ID
   * @param userMessages User messages from Redux store
   * @param messageLogs Message logs from Redux store
   * @returns Relevance result with matched message ID
   */
  static isMessageRelevant(
    waMessageId: any,
    userMessages: any[],
    messageLogs: any[]
  ): MessageRelevanceResult {
    const components = this.parseWhatsAppMessageId(waMessageId);
    if (!components) {
      return {
        isRelevant: false,
        matchType: 'no_match',
        confidence: 0
      };
    }

    // Only interested in outgoing messages (messages we sent)
    if (!components.fromMe) {
      return {
        isRelevant: false,
        matchType: 'no_match',
        confidence: 0
      };
    }

    return this.findMessageByComponents(components, userMessages, messageLogs);
  }

  /**
   * Create group message tracking for recipient-level status updates
   * @param messageId Our internal message ID
   * @param chatId WhatsApp chat ID
   * @param totalRecipients Total number of recipients
   * @returns Group message tracking object
   */
  static createGroupMessageTracking(
    messageId: string,
    chatId: string,
    totalRecipients: number
  ): GroupMessageTracking {
    return {
      messageId,
      chatId,
      totalRecipients,
      recipientStatuses: [],
      aggregatedCounts: {
        sent: 0,
        delivered: 0,
        read: 0,
        played: 0
      }
    };
  }

  /**
   * Update recipient status in group message tracking
   * @param tracking Group message tracking object
   * @param recipientId Recipient identifier
   * @param status New status
   * @returns Updated tracking object
   */
  static updateRecipientStatus(
    tracking: GroupMessageTracking,
    recipientId: string,
    status: RecipientMessageStatus['status']
  ): GroupMessageTracking {
    const existingIndex = tracking.recipientStatuses.findIndex(
      r => r.recipientId === recipientId
    );

    const newStatus: RecipientMessageStatus = {
      recipientId,
      status,
      timestamp: new Date().toISOString()
    };

    if (existingIndex >= 0) {
      tracking.recipientStatuses[existingIndex] = newStatus;
    } else {
      tracking.recipientStatuses.push(newStatus);
    }

    // Recalculate aggregated counts
    tracking.aggregatedCounts = tracking.recipientStatuses.reduce(
      (counts, recipient) => {
        counts[recipient.status]++;
        return counts;
      },
      { sent: 0, delivered: 0, read: 0, played: 0 }
    );

    return tracking;
  }

  /**
   * Get the most advanced status for a message based on recipient statuses
   * @param tracking Group message tracking object
   * @returns Most advanced status
   */
  static getMostAdvancedStatus(tracking: GroupMessageTracking): string {
    const { aggregatedCounts } = tracking;
    
    if (aggregatedCounts.played > 0) return 'played';
    if (aggregatedCounts.read > 0) return 'read';
    if (aggregatedCounts.delivered > 0) return 'delivered';
    return 'sent';
  }

  /**
   * Enhanced method to detect group messages and extract recipient information
   * @param waComponents WhatsApp message components
   * @param message Optional WhatsApp message object for additional context
   * @returns Enhanced message info with group/recipient details
   */
  static analyzeMessageContext(waComponents: WhatsAppMessageComponents, message?: any): {
    isGroupMessage: boolean;
    recipientCount: number;
    chatType: 'individual' | 'group' | 'broadcast';
    recipientIds?: string[];
  } {
    // Handle chatId as either string or object
    const chatIdString = typeof waComponents.chatId === 'string' 
      ? waComponents.chatId 
      : waComponents.chatId?._serialized || '';
    
    const kind = chatKindOf(chatIdString);
    const isGroupMessage = kind === 'group';
    // Status and broadcast lists: both carry their recipients on `to.participants`.
    const isBroadcast = kind === 'status' || kind === 'broadcast';
    
    let recipientCount = 1;
    let recipientIds: string[] = [];
    let chatType: 'individual' | 'group' | 'broadcast' = 'individual';

    if (isBroadcast) {
      chatType = 'broadcast';
      // For broadcast messages, we'd need to get the recipient list from the message
      if (message?.to?.participants) {
        recipientCount = message.to.participants.length;
        recipientIds = message.to.participants.map((p: any) => p.id?._serialized || p.id);
      }
    } else if (isGroupMessage) {
      chatType = 'group';
      // For group messages, we'd need to get the group participant count
      if (message?.to?.participants) {
        recipientCount = message.to.participants.length;
        recipientIds = message.to.participants.map((p: any) => p.id?._serialized || p.id);
      } else {
        // Fallback: try to estimate from ack data if available
        recipientCount = 1; // Default to 1 if we can't determine
      }
    }

    return {
      isGroupMessage,
      recipientCount,
      chatType,
      recipientIds: recipientIds.length > 0 ? recipientIds : undefined
    };
  }

  /**
   * Create enhanced message update with recipient-level tracking
   * @param waMessageId WhatsApp message ID
   * @param ackData Acknowledgment data
   * @param message Optional WhatsApp message object
   * @returns Enhanced message update with recipient tracking
   */
  static createEnhancedMessageUpdate(
    waMessageId: any,
    ackData: any,
    message?: any
  ): (MessageAckUpdate & { recipientTracking?: GroupMessageTracking }) | null {
    const components = this.parseWhatsAppMessageId(waMessageId);
    if (!components) return null;

    const context = this.analyzeMessageContext(components, message);
    
    const baseUpdate: MessageAckUpdate = {
      messageId: '', // Will be filled by the caller
      waMessageKey: waMessageId,
      ackData
    };

    // If it's a group message, add recipient tracking
    if (context.isGroupMessage && context.recipientCount > 1) {
      const recipientTracking = this.createGroupMessageTracking(
        '', // messageId will be filled by caller
        typeof components.chatId === 'string' ? components.chatId : components.chatId._serialized,
        context.recipientCount
      );

      // If we have specific recipient IDs, initialize their statuses
      if (context.recipientIds) {
        context.recipientIds.forEach(recipientId => {
          // Determine status based on ackData
          let status: RecipientMessageStatus['status'] = 'sent';
          if (ackData.playedRemaining === 0) status = 'played';
          else if (ackData.readRemaining === 0) status = 'read';
          else if (ackData.deliveryRemaining === 0) status = 'delivered';

          this.updateRecipientStatus(recipientTracking, recipientId, status);
        });
      }

      return {
        ...baseUpdate,
        recipientTracking
      };
    }

    return baseUpdate;
  }

  /**
   * Get status level (moved from message-log-update-service for consistency)
   */
  private static getStatusLevel(status: string): number {
    const STATUS_LEVEL_MAP = new Map([
      ['played', 0],
      ['read', 1],
      ['delivered', 2],
      ['sent', 3],
      ['confirmed', 3],
      ['bt-sent', 3],
      ['pending', 4],
      ['before-wa-send', 4],
      ['failed', 5],
      ['error', 5],
      ['cancelled', 5]
    ]);

    return STATUS_LEVEL_MAP.get(status) ?? 99;
  }

  /**
   * Debug helper to log message ID components
   * @param waMessageId WhatsApp message ID
   * @param label Debug label
   */
  static debugMessageId(waMessageId: any, label: string = 'MessageID'): void {
    const components = this.parseWhatsAppMessageId(waMessageId);
    const context = components ? this.analyzeMessageContext(components) : null;
    
    console.log(`[WhatsAppMessageIdService] ${label}:`, {
      original: waMessageId,
      parsed: components,
      context: context
    });
  }
}
